/* eslint-disable react/no-unused-prop-types */
import React, { useState, useEffect, useMemo } from "react";
import PropTypes from "prop-types";
import { isChildType } from "../util/isChildType";
import { KeyCode } from "../util/keyCodes";
import { TabList } from "./Tab/TabList";
import { Panels } from "./Panel/Panels";

const Tab = () => null;
Tab.displayName = "Tab";

const processTabData = children =>
	React.Children.map(children, child => {
		if (isChildType(child, Tab)) {
			return {
				label: child.props.title,
				subLabel: child.props.subTitle,
				newLabel: child.props.newLabel,
				icon: child.props.icon,
				content: child.props.children,
				eventKey: child.props.eventKey,
				onCustomClick: child.props.onHandleCustomClick,
				onCustomKeys: child.props.onHandleCustomKeys,
			};
		}
		return undefined;
	});

const Tabs = ({
	activeKey,
	children,
	className,
	activeClassName,
	tabClassName,
	panelClassName,
	tabsWrapperClassName,
	tabsRightButtonClassName,
	tabsLeftButtonClassName,
	tabIconClassName,
}) => {
	const data = useMemo(() => processTabData(children), [children]);
	const [selectedKey, setSelectedKey] = useState(activeKey);
	const [focusedKey, setFocusedKey] = useState(null);
	const [isFocused, setIsFocused] = useState(false);

	const setFocus = key => {
		setFocusedKey(key);
		setIsFocused(true);
	};

	const handleClick = (e, key) => {
		if (e) e.preventDefault();

		// Execute custom onClick handler
		let isPropagated = true;
		data
			.filter(tabItem => tabItem.eventKey === key)
			.forEach(tabItem => {
				if (typeof tabItem.onCustomClick === "function") {
					// Return false to disable switching tabs
					isPropagated = tabItem.onCustomClick(e);
				}
			});

		if (selectedKey === key || !isPropagated) {
			return;
		}
		setSelectedKey(key);
		setFocus(key);
	};

	const handleKeyDown = e => {
		if (e.keyCode === KeyCode.CODE_LEFT || e.keyCode === KeyCode.CODE_RIGHT) {
			e.preventDefault();
		} else {
			return;
		}
		const length = data?.length;
		const index = data.findIndex(item => item.eventKey === selectedKey);
		let targetIndex;

		if (e.keyCode === KeyCode.CODE_LEFT && index > 0) {
			targetIndex = index - 1;
		} else if (e.keyCode === KeyCode.CODE_RIGHT && index < length - 1) {
			targetIndex = index + 1;
		} else {
			return;
		}

		if (typeof data[targetIndex].onCustomKeys === "function") {
			data[targetIndex].onCustomKeys(e);
		}

		setFocus(data[targetIndex].eventKey);
		setSelectedKey(data[targetIndex].eventKey);
	};

	const hashChangeHandler = () => {
		const newHash = decodeURI(window.location.hash.substring(1).replace(/\s/g, "-").toLowerCase());

		if (newHash && data.find(tab => tab.eventKey === newHash)) {
			setSelectedKey(newHash);
			handleClick(null, newHash);

			return true;
		}

		return false;
	};

	useEffect(() => {
		if (!hashChangeHandler()) {
			setSelectedKey(activeKey || data?.[0]?.eventKey);
		}

		window.addEventListener("hashchange", hashChangeHandler);
		return () => {
			window.removeEventListener("hashchange", hashChangeHandler);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (activeKey && activeKey !== selectedKey) {
			setSelectedKey(activeKey);
			setFocus(activeKey);
		}

		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [activeKey]);

	if (data?.length === 0) return <></>;

	return (
		<>
			<TabList
				data={data}
				selectedKey={selectedKey}
				onClick={handleClick}
				onKeyDown={handleKeyDown}
				className={className}
				activeClassName={activeClassName}
				tabClassName={tabClassName}
				focusedKey={focusedKey}
				isFocused={isFocused}
				tabsWrapperClassName={tabsWrapperClassName}
				tabsRightButtonClassName={tabsRightButtonClassName}
				tabsLeftButtonClassName={tabsLeftButtonClassName}
				tabIconClassName={tabIconClassName}
			/>
			<Panels data={data} selectedKey={selectedKey} className={panelClassName} />
		</>
	);
};

Tabs.defaultProps = {
	className: "",
	activeClassName: "",
	tabClassName: "",
	activeKey: undefined,
	panelClassName: "",
	tabsWrapperClassName: "",
	tabsRightButtonClassName: "",
	tabsLeftButtonClassName: "",
	tabIconClassName: "",
};

Tabs.propTypes = {
	/**
	 * Class that styles `<nav>` of tabs list.
	 */
	className: PropTypes.string,
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
	/**
	 * Class that styles active tab
	 */
	activeClassName: PropTypes.string,
	/**
	 * Class that styles `<a>` of tabs list
	 */
	tabClassName: PropTypes.string,
	/**
	 * Class that styles `<div>` of panel wrapper
	 */
	panelClassName: PropTypes.string,
	/**
	 * The Tab, that will be active by default. If this value is not select, the first Tab will be activated by default.
	 */
	activeKey: PropTypes.string,

	tabsWrapperClassName: PropTypes.string,
	tabsRightButtonClassName: PropTypes.string,
	tabsLeftButtonClassName: PropTypes.string,
	tabIconClassName: PropTypes.string,
};
Tab.propTypes = {
	children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]).isRequired,
	eventKey: PropTypes.string.isRequired,
	title: PropTypes.node.isRequired,
	icon: PropTypes.object,
};
Tab.defaultProps = {
	icon: null,
};

export default Tabs;
export { Tabs, Tab };
